import {INITIAL_GDN_TAGS, INITIAL_GDN_TAG_ORDER, nextGdnTagId, assertGdnTagId} from "../libs/gdnTags";
import {nextGdnColorVariant, getGdnColorStateKey} from "../libs/gdnColorCycle";
import {fromStoredEditableText} from "../data/editable-text";
import {ALL_CONTENT_FIELDS} from "./legacy";
import {type BannerInstance, type BannerPatch, type BannerAppearance} from "./document";
import {resolveBannerSelection} from "../data/template-availability";
import {variantsForLayout} from "../data/products/index";

// Native layout buttons select a complete valid treatment. The HTTP patch
// contract still requires callers to supply a compatible variant explicitly.
export function createLayoutPatch(banner: BannerInstance, layout: number): BannerPatch {
  const {product} = resolveBannerSelection(banner);
  const variants = variantsForLayout(product.code, banner.set ?? undefined, layout);
  const variant = banner.appearance.variant;
  return {appearance: {layout, ...(variant !== undefined && variants && !variants.some((entry) => entry.id === variant)
    ? {variant: variants[0].id} : {})}};
}

export type BannerUpdate = BannerPatch | ((banner: BannerInstance) => BannerPatch);
export type BannerChange = (instanceId: string, update: BannerUpdate) => void;

const EMPTY_SLOT = {url: null, x: 0, y: 0, scale: 1, rotate: 0, flip: {x: false, y: false}};
export function imageSlotForKey(key: string, instanceId: string): string | null {
  if (key === "main" || key === instanceId) return null;
  if (/^(speaker-[12]|ls-speaker-[1-6]|custom-qr-[123])$/.test(key)) return key;
  for (const slot of [...Array.from({length: 6}, (_, i) => `ls-speaker-${i + 1}`), "speaker-1", "speaker-2", "custom-qr-1", "custom-qr-2", "custom-qr-3"]) {
    if (key === `${instanceId}-${slot}`) return slot;
  }
  for (const slot of ["overlay", "cursor"]) if (key === `${slot}-${instanceId}`) return slot;
  throw new Error(`Unknown image slot: ${key}`);
}

// Compatibility setters read the current document for functional/partial updates.
// IDs are bound at construction, so selection cannot redirect an asynchronous completion.
export function createBannerWriters(getBanner: () => BannerInstance, write: (patch: BannerUpdate) => void) {
  const instanceId = getBanner().instanceId;
  const appearance = (field: keyof BannerAppearance) => (value: unknown) => write({appearance: {[field]: value}});
  const keyedAppearance = (field: keyof BannerAppearance) => (id: string, value: unknown) => {
    if (id !== instanceId && id !== "main") throw new Error(`Unknown banner key: ${id}`);
    appearance(field)(value);
  };
  const slotWrite = (key: string, patch: Record<string, unknown>) => {
    const slot = imageSlotForKey(key, instanceId);
    if (slot === null) throw new Error("Expected a named image slot");
    write((banner) => {
    const current = banner.assets.slots[slot] ?? {...EMPTY_SLOT, ...(slot === "cursor" ? {x: 200, y: 200} : slot === "overlay" ? {x: 100, y: 100} : {})};
    return {assets: {slots: {...banner.assets.slots, [slot]: {...current, ...patch}}}};
    });
  };
  const writers: Record<string, (...args: any[]) => void> = {};
  for (const [field, legacy] of Object.entries(ALL_CONTENT_FIELDS)) {
    const name = legacy === "productNames" ? "editProductName" : `edit${legacy[0].toUpperCase()}${legacy.slice(1)}`;
    writers[name] = (value: string, key = instanceId) => {
      if (field === "subtitle" && key === `cursor-${instanceId}`) write({content: {cursorLabel: fromStoredEditableText(value)}});
      else {
        if (key !== instanceId && key !== "main") throw new Error(`Unknown text key: ${key}`);
        write({content: {[field]: fromStoredEditableText(value)}});
      }
    };
  }
  const scalars = {
    editVariantOption: "variant", editTitleFontSize: "titleSize", editIsButtonEnabled: "buttonEnabled", editGdnAccentColor: "gdnAccentColor", editFontOption: "font", editIconOption: "icon", editBase: "base",
    editIsFontToggle: "fontToggle", toggleUppercase: "uppercase", editFontVariation: "fontVariation",
    editLogoHidden: "logoHidden", editBgBrightness: "backgroundBrightness", editBgBlurValue: "backgroundBlur",
    editBgOpacity: "backgroundOpacity",
    editBgOption: "backgroundOption", editGenerativeGradients: "generative", editGenerativeSpeed: "generativeSpeed",
    editGenerativeCount: "generativeCount", editGenerativeStops: "generativeStops",
  } as const;
  for (const [name, field] of Object.entries(scalars)) writers[name] = appearance(field);
  writers.editLayoutOption = (layout: number) => write((banner) => createLayoutPatch(banner, layout));
  writers.editGdnTags = (value: string, key: string) => {
    assertGdnTagId(key);
    write((banner) => ({content: {gdnTags: {...(banner.content.gdnTags ?? INITIAL_GDN_TAGS), [key]: fromStoredEditableText(value)},
      gdnTagOrder: [...(banner.content.gdnTagOrder ?? INITIAL_GDN_TAG_ORDER)]}}));
  };
  writers.addGdnTag = (value: string) => write((banner) => {
    const tags = banner.content.gdnTags ?? INITIAL_GDN_TAGS;
    const id = nextGdnTagId(tags);
    return {content: {gdnTags: {...tags, [id]: fromStoredEditableText(value)}, gdnTagOrder: [...(banner.content.gdnTagOrder ?? INITIAL_GDN_TAG_ORDER), id]}};
  });
  writers.removeGdnTag = (id: string) => write((banner) => {
    assertGdnTagId(id);
    const tags = {...(banner.content.gdnTags ?? INITIAL_GDN_TAGS)};
    delete tags[id];
    return {content: {gdnTags: tags, gdnTagOrder: (banner.content.gdnTagOrder ?? INITIAL_GDN_TAG_ORDER).filter((key) => key !== id)}};
  });
  writers.cycleGdnColor = (id: string) => write((banner) => {
    const key = getGdnColorStateKey(id), colors = banner.appearance.gdnColorVariants ?? {};
    return {appearance: {gdnColorVariants: {...colors, [key]: nextGdnColorVariant(colors[key])}}};
  });
  writers.clearGdnColor = (id: string) => write((banner) => {
    const key = getGdnColorStateKey(id), colors = {...banner.appearance.gdnColorVariants};
    delete colors[key];
    return {appearance: {gdnColorVariants: colors}};
  });
  writers.editBgOpacities = keyedAppearance("backgroundOpacity");
  writers.editBgBrightnesses = keyedAppearance("backgroundBrightness");
  writers.editButtonColor = keyedAppearance("buttonColor");
  for (const [prefix, target] of [["Bg", "background"], ["Logo", "logo"]] as const) {
    for (const suffix of prefix === "Bg" ? ["X", "Y", "Scale", "Rotate", "Flip"] : ["X", "Y", "Scale"]) {
      writers[`edit${prefix}${suffix}`] = keyedAppearance(`${target}${suffix}` as keyof BannerAppearance);
    }
  }
  for (const suffix of ["X", "Y", "Scale", "Rotate", "Flip"] as const) {
    writers[`editUpload${suffix}`] = (key: string, value: unknown) => {
      if (imageSlotForKey(key, instanceId) === null) appearance(`upload${suffix}`)(value);
      else slotWrite(key, {[suffix.toLowerCase()]: value});
    };
  }
  writers.editUploadImages = (key: string, url: string | null | undefined) => {
    if (imageSlotForKey(key, instanceId) === null) write({assets: {image: url || null}});
    else slotWrite(key, {url: url || null});
  };
  writers.removeUploadImages = (key: string) => writers.editUploadImages(key, null);
  writers.editLogos = (key: string, url: string | null) => {
    imageSlotForKey(key, instanceId);
    write({assets: {logo: url || null}});
  };
  writers.editLogoVariant = (key: string, logoVariant: string | null) => {
    if (key !== instanceId) throw new Error(`Unknown logo key: ${key}`);
    write({assets: {logoVariant}});
  };
  writers.editTextColorLocked = keyedAppearance("textColor");
  writers.setTags = (value: BannerInstance["content"]["tags"] | ((tags: BannerInstance["content"]["tags"]) => BannerInstance["content"]["tags"])) =>
    write((banner) => ({content: {tags: typeof value === "function" ? value(banner.content.tags) : value}}));
  writers.editCustomBg = (url: string | null) => write({assets: {background: url || null}, appearance: {backgroundOption: url ? "custom" : "1"}});
  writers.editCustomBgs = (_key: string, url: string | null) => writers.editCustomBg(url);
  return writers;
}
